/**
 * Penilaian Individu API client
 */

import apiClient from '@/core/api/client'
import { API_ENDPOINTS } from '@/core/api/endpoints'
import type {
  SSIndicator,
  SSQuestion,
  AkhlakValue,
  AkhlakBehavior,
  SSIndicatorCreate,
  SSIndicatorUpdate,
  SSQuestionCreate,
  SSQuestionUpdate,
  AkhlakValueCreate,
  AkhlakValueUpdate,
  AkhlakBehaviorCreate,
  AkhlakBehaviorUpdate,
  AssessmentPeriod,
  KPIDefinition,
  KPIAssignment,
  PeerAssignment,
  AssessmentResult,
  AssessmentApproval,
  PeriodsResponse,
  AssignmentsResponse,
  ResultsResponse,
  EmployeeOverviewResponse,
  CreatePeriodInput,
  UpdatePeriodInput,
  CreateKPIInput,
  AssignKPIInput,
  UpdateKPIDefinitionInput,
  UpdateKPIAssignmentInput,
  UpdateRealisasiInput,
  BulkCreateAssignmentsInput,
  SubmitAssessmentInput,
  UpdateNarrativeInput,
  ApprovalActionInput,
  PeerAssignmentItem,
  AssignmentResponses,
  KPIPlan,
  TeamKPIPlanRow,
  CreateMyKPIInput,
  CompanyKPIRow,
  CreateCompanyKPIInput,
  UpdateCompanyKPIInput,
  UpdateCompanyKPIRealisasiInput,
} from '../types/penilaian.types'

export const penilaianApi = {
  // ==================== Master Data ====================

  getSSIndicators: async (): Promise<SSIndicator[]> => {
    const res = await apiClient.get<SSIndicator[]>(API_ENDPOINTS.PENILAIAN.SS_INDICATORS)
    return res.data
  },

  getAkhlakValues: async (): Promise<AkhlakValue[]> => {
    const res = await apiClient.get<AkhlakValue[]>(API_ENDPOINTS.PENILAIAN.AKHLAK_VALUES)
    return res.data
  },

  // SS Indicator CRUD
  createSSIndicator: async (data: SSIndicatorCreate): Promise<SSIndicator> => {
    const res = await apiClient.post<SSIndicator>(API_ENDPOINTS.PENILAIAN.SS_INDICATORS, data)
    return res.data
  },
  updateSSIndicator: async (id: string, data: SSIndicatorUpdate): Promise<SSIndicator> => {
    const res = await apiClient.put<SSIndicator>(API_ENDPOINTS.PENILAIAN.SS_INDICATOR(id), data)
    return res.data
  },
  deleteSSIndicator: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.SS_INDICATOR(id))
  },

  // SS Question CRUD
  createSSQuestion: async (indicatorId: string, data: SSQuestionCreate): Promise<SSQuestion> => {
    const res = await apiClient.post<SSQuestion>(
      API_ENDPOINTS.PENILAIAN.SS_INDICATOR_QUESTIONS(indicatorId), data
    )
    return res.data
  },
  updateSSQuestion: async (id: string, data: SSQuestionUpdate): Promise<SSQuestion> => {
    const res = await apiClient.put<SSQuestion>(API_ENDPOINTS.PENILAIAN.SS_QUESTION(id), data)
    return res.data
  },
  deleteSSQuestion: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.SS_QUESTION(id))
  },

  // AKHLAK Value CRUD
  createAkhlakValue: async (data: AkhlakValueCreate): Promise<AkhlakValue> => {
    const res = await apiClient.post<AkhlakValue>(API_ENDPOINTS.PENILAIAN.AKHLAK_VALUES, data)
    return res.data
  },
  updateAkhlakValue: async (id: string, data: AkhlakValueUpdate): Promise<AkhlakValue> => {
    const res = await apiClient.put<AkhlakValue>(API_ENDPOINTS.PENILAIAN.AKHLAK_VALUE(id), data)
    return res.data
  },
  deleteAkhlakValue: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.AKHLAK_VALUE(id))
  },

  // AKHLAK Behavior CRUD
  createAkhlakBehavior: async (valueId: string, data: AkhlakBehaviorCreate): Promise<AkhlakBehavior> => {
    const res = await apiClient.post<AkhlakBehavior>(
      API_ENDPOINTS.PENILAIAN.AKHLAK_VALUE_BEHAVIORS(valueId), data
    )
    return res.data
  },
  updateAkhlakBehavior: async (id: string, data: AkhlakBehaviorUpdate): Promise<AkhlakBehavior> => {
    const res = await apiClient.put<AkhlakBehavior>(API_ENDPOINTS.PENILAIAN.AKHLAK_BEHAVIOR(id), data)
    return res.data
  },
  deleteAkhlakBehavior: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.AKHLAK_BEHAVIOR(id))
  },

  // ==================== Periods ====================

  getPeriods: async (status?: string): Promise<PeriodsResponse> => {
    const res = await apiClient.get<PeriodsResponse>(API_ENDPOINTS.PENILAIAN.PERIODS, {
      params: status ? { status } : undefined,
    })
    return res.data
  },

  createPeriod: async (data: CreatePeriodInput): Promise<AssessmentPeriod> => {
    const res = await apiClient.post<AssessmentPeriod>(API_ENDPOINTS.PENILAIAN.PERIODS, data)
    return res.data
  },

  getPeriod: async (id: string): Promise<AssessmentPeriod> => {
    const res = await apiClient.get<AssessmentPeriod>(API_ENDPOINTS.PENILAIAN.PERIOD(id))
    return res.data
  },

  updatePeriod: async (id: string, data: UpdatePeriodInput): Promise<AssessmentPeriod> => {
    const res = await apiClient.patch<AssessmentPeriod>(API_ENDPOINTS.PENILAIAN.PERIOD(id), data)
    return res.data
  },

  // ==================== KPI Management ====================

  getPeriodKPIs: async (
    periodId: string,
    params?: { department?: string; kpi_type?: string }
  ): Promise<KPIDefinition[]> => {
    const res = await apiClient.get<KPIDefinition[]>(
      API_ENDPOINTS.PENILAIAN.PERIOD_KPIS(periodId),
      { params }
    )
    return res.data
  },

  createKPI: async (periodId: string, data: CreateKPIInput): Promise<KPIDefinition> => {
    const res = await apiClient.post<KPIDefinition>(
      API_ENDPOINTS.PENILAIAN.PERIOD_KPIS(periodId),
      data
    )
    return res.data
  },

  getEmployeeKPIAssignments: async (
    periodId: string,
    employeeId: string
  ): Promise<KPIAssignment[]> => {
    const res = await apiClient.get<KPIAssignment[]>(
      API_ENDPOINTS.PENILAIAN.EMPLOYEE_KPIS(periodId, employeeId)
    )
    return res.data
  },

  assignKPI: async (data: AssignKPIInput): Promise<KPIAssignment> => {
    const res = await apiClient.post<KPIAssignment>(
      API_ENDPOINTS.PENILAIAN.KPI_ASSIGNMENTS,
      data
    )
    return res.data
  },

  updateKPIDefinition: async (
    kpiId: string,
    data: UpdateKPIDefinitionInput
  ): Promise<KPIDefinition> => {
    const res = await apiClient.patch<KPIDefinition>(
      API_ENDPOINTS.PENILAIAN.KPI_DEFINITION(kpiId),
      data
    )
    return res.data
  },

  updateKPIAssignment: async (
    assignmentId: string,
    data: UpdateKPIAssignmentInput
  ): Promise<KPIAssignment> => {
    const res = await apiClient.patch<KPIAssignment>(
      API_ENDPOINTS.PENILAIAN.KPI_ASSIGNMENT(assignmentId),
      data
    )
    return res.data
  },

  deleteKPIAssignment: async (assignmentId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.KPI_ASSIGNMENT(assignmentId))
  },

  updateRealisasi: async (
    assignmentId: string,
    data: UpdateRealisasiInput
  ): Promise<KPIAssignment> => {
    const res = await apiClient.patch<KPIAssignment>(
      API_ENDPOINTS.PENILAIAN.KPI_REALISASI(assignmentId),
      data
    )
    return res.data
  },

  // ==================== Peer Assignments ====================

  getPeriodAssignments: async (
    periodId: string,
    params?: { role?: string }
  ): Promise<AssignmentsResponse> => {
    const res = await apiClient.get<AssignmentsResponse>(
      API_ENDPOINTS.PENILAIAN.PERIOD_ASSIGNMENTS(periodId),
      { params }
    )
    return res.data
  },

  getAssignment: async (assignmentId: string): Promise<PeerAssignment> => {
    const res = await apiClient.get<PeerAssignment>(API_ENDPOINTS.PENILAIAN.ASSIGNMENT(assignmentId))
    return res.data
  },

  getMyAssignments: async (periodId: string): Promise<AssignmentsResponse> => {
    const res = await apiClient.get<AssignmentsResponse>(
      API_ENDPOINTS.PENILAIAN.MY_ASSIGNMENTS(periodId)
    )
    return res.data
  },

  bulkCreateAssignments: async (
    periodId: string,
    data: BulkCreateAssignmentsInput
  ): Promise<PeerAssignment[]> => {
    const res = await apiClient.post<PeerAssignment[]>(
      API_ENDPOINTS.PENILAIAN.BULK_ASSIGN(periodId),
      data
    )
    return res.data
  },

  autoAssignDivision: async (
    periodId: string,
    division: string
  ): Promise<{ data: PeerAssignment[]; total: number }> => {
    const res = await apiClient.post<{ data: PeerAssignment[]; total: number }>(
      API_ENDPOINTS.PENILAIAN.AUTO_ASSIGN(periodId, division)
    )
    return res.data
  },

  autoAssignAtasan: async (
    periodId: string
  ): Promise<{ data: PeerAssignment[]; total: number }> => {
    const res = await apiClient.post<{ data: PeerAssignment[]; total: number }>(
      API_ENDPOINTS.PENILAIAN.AUTO_ASSIGN_ATASAN(periodId)
    )
    return res.data
  },

  autoAssignBawahan: async (
    periodId: string
  ): Promise<{ data: PeerAssignment[]; total: number }> => {
    const res = await apiClient.post<{ data: PeerAssignment[]; total: number }>(
      API_ENDPOINTS.PENILAIAN.AUTO_ASSIGN_BAWAHAN(periodId)
    )
    return res.data
  },

  deleteAssignment: async (assignmentId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.DELETE_ASSIGNMENT(assignmentId))
  },

  getEmployeeOverview: async (periodId: string): Promise<EmployeeOverviewResponse> => {
    const res = await apiClient.get<EmployeeOverviewResponse>(
      API_ENDPOINTS.PENILAIAN.EMPLOYEE_OVERVIEW(periodId)
    )
    return res.data
  },

  addSingleAssignment: async (
    periodId: string,
    item: PeerAssignmentItem
  ): Promise<PeerAssignment[]> => {
    const res = await apiClient.post<PeerAssignment[]>(
      API_ENDPOINTS.PENILAIAN.BULK_ASSIGN(periodId),
      { period_id: periodId, assignments: [item] }
    )
    return res.data
  },

  // ==================== Assessment Submission ====================

  getAssignmentResponses: async (assignmentId: string): Promise<AssignmentResponses> => {
    const res = await apiClient.get<AssignmentResponses>(
      API_ENDPOINTS.PENILAIAN.ASSIGNMENT_RESPONSES(assignmentId)
    )
    return res.data
  },

  submitAssessment: async (
    assignmentId: string,
    data: SubmitAssessmentInput
  ): Promise<PeerAssignment> => {
    const res = await apiClient.post<PeerAssignment>(
      API_ENDPOINTS.PENILAIAN.SUBMIT_ASSESSMENT(assignmentId),
      data
    )
    return res.data
  },

  // ==================== Score Computation ====================

  computeScores: async (
    periodId: string,
    employeeId: string
  ): Promise<AssessmentResult> => {
    const res = await apiClient.post<AssessmentResult>(
      API_ENDPOINTS.PENILAIAN.COMPUTE_SCORES(periodId, employeeId)
    )
    return res.data
  },

  getEmployeeSubmittedAssignments: async (
    periodId: string,
    employeeId: string
  ): Promise<AssignmentsResponse> => {
    const res = await apiClient.get<AssignmentsResponse>(
      API_ENDPOINTS.PENILAIAN.EMPLOYEE_SUBMITTED_ASSIGNMENTS(periodId, employeeId)
    )
    return res.data
  },

  computeAllScores: async (periodId: string): Promise<ResultsResponse> => {
    const res = await apiClient.post<ResultsResponse>(
      API_ENDPOINTS.PENILAIAN.COMPUTE_ALL_SCORES(periodId)
    )
    return res.data
  },

  // ==================== Results ====================

  getMyResult: async (params?: { period_id?: string }): Promise<ResultsResponse> => {
    const res = await apiClient.get<ResultsResponse>(API_ENDPOINTS.PENILAIAN.MY_RESULT, { params })
    return res.data
  },

  getMySubmittedAssessments: async (params?: { period_id?: string }): Promise<AssignmentsResponse> => {
    const res = await apiClient.get<AssignmentsResponse>(
      API_ENDPOINTS.PENILAIAN.MY_SUBMITTED_ASSESSMENTS, { params }
    )
    return res.data
  },

  getResults: async (params?: { period_id?: string }): Promise<ResultsResponse> => {
    const res = await apiClient.get<ResultsResponse>(API_ENDPOINTS.PENILAIAN.RESULTS, { params })
    return res.data
  },

  getTeamResults: async (params?: { period_id?: string }): Promise<ResultsResponse> => {
    const res = await apiClient.get<ResultsResponse>(API_ENDPOINTS.PENILAIAN.TEAM_RESULTS, { params })
    return res.data
  },

  getResult: async (id: string): Promise<AssessmentResult> => {
    const res = await apiClient.get<AssessmentResult>(API_ENDPOINTS.PENILAIAN.RESULT(id))
    return res.data
  },

  updateNarrative: async (
    id: string,
    data: UpdateNarrativeInput
  ): Promise<AssessmentResult> => {
    const res = await apiClient.patch<AssessmentResult>(
      API_ENDPOINTS.PENILAIAN.RESULT(id),
      data
    )
    return res.data
  },

  // ==================== Approvals ====================

  processApproval: async (
    resultId: string,
    level: number,
    data: ApprovalActionInput
  ): Promise<AssessmentApproval> => {
    const res = await apiClient.post<AssessmentApproval>(
      API_ENDPOINTS.PENILAIAN.APPROVE(resultId, level),
      data
    )
    return res.data
  },

  hrReviewResult: async (
    resultId: string,
    data: ApprovalActionInput
  ): Promise<AssessmentResult> => {
    const res = await apiClient.post<AssessmentResult>(
      API_ENDPOINTS.PENILAIAN.HR_REVIEW(resultId),
      data
    )
    return res.data
  },

  // ==================== KPI Plans ====================

  getMyKPIPlan: async (year: number): Promise<KPIPlan> => {
    const res = await apiClient.get<KPIPlan>(API_ENDPOINTS.PENILAIAN.KPI_PLANS_MY, {
      params: { year },
    })
    return res.data
  },

  submitMyKPIPlan: async (year: number): Promise<KPIPlan> => {
    const res = await apiClient.post<KPIPlan>(
      API_ENDPOINTS.PENILAIAN.KPI_PLANS_MY_SUBMIT,
      null,
      { params: { year } }
    )
    return res.data
  },

  getTeamKPIPlans: async (year: number, viewAll = false): Promise<TeamKPIPlanRow[]> => {
    const res = await apiClient.get<TeamKPIPlanRow[]>(
      API_ENDPOINTS.PENILAIAN.KPI_PLANS_TEAM,
      { params: { year, view: viewAll ? 'all' : 'team' } }
    )
    return res.data
  },

  approveKPIPlan: async (planId: string): Promise<KPIPlan> => {
    const res = await apiClient.post<KPIPlan>(API_ENDPOINTS.PENILAIAN.KPI_PLAN_APPROVE(planId))
    return res.data
  },

  rejectKPIPlan: async (planId: string, comment: string): Promise<KPIPlan> => {
    const res = await apiClient.post<KPIPlan>(
      API_ENDPOINTS.PENILAIAN.KPI_PLAN_REJECT(planId),
      { comment }
    )
    return res.data
  },

  createEmployeeKPI: async (data: CreateMyKPIInput): Promise<KPIAssignment[]> => {
    const res = await apiClient.post<KPIAssignment[]>(
      API_ENDPOINTS.PENILAIAN.EMPLOYEE_KPI_CREATE,
      data
    )
    return res.data
  },

  getEmployeeKPIsAnnual: async (
    employeeId: string,
    year: number
  ): Promise<Record<number, KPIAssignment[]>> => {
    const res = await apiClient.get<Record<number, KPIAssignment[]>>(
      API_ENDPOINTS.PENILAIAN.EMPLOYEE_KPIS_ANNUAL(employeeId),
      { params: { year } }
    )
    return res.data
  },

  // ==================== Company KPI Global Management ====================

  getCompanyKPIs: async (year: number): Promise<CompanyKPIRow[]> => {
    const res = await apiClient.get<CompanyKPIRow[]>(
      API_ENDPOINTS.PENILAIAN.COMPANY_KPIS,
      { params: { year } }
    )
    return res.data
  },

  createCompanyKPI: async (data: CreateCompanyKPIInput): Promise<KPIDefinition[]> => {
    const res = await apiClient.post<KPIDefinition[]>(
      API_ENDPOINTS.PENILAIAN.COMPANY_KPIS,
      data
    )
    return res.data
  },

  updateCompanyKPI: async (
    name: string,
    year: number,
    data: UpdateCompanyKPIInput
  ): Promise<KPIDefinition[]> => {
    const res = await apiClient.patch<KPIDefinition[]>(
      API_ENDPOINTS.PENILAIAN.COMPANY_KPIS,
      data,
      { params: { name, year } }
    )
    return res.data
  },

  deleteCompanyKPI: async (name: string, year: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.PENILAIAN.COMPANY_KPIS, {
      params: { name, year },
    })
  },

  syncCompanyKPIAssignments: async (year: number): Promise<{ created: number }> => {
    const res = await apiClient.post<{ created: number }>(
      API_ENDPOINTS.PENILAIAN.COMPANY_KPIS_SYNC,
      null,
      { params: { year } }
    )
    return res.data
  },

  updateCompanyKPIRealisasi: async (data: UpdateCompanyKPIRealisasiInput): Promise<{ updated: number }> => {
    const res = await apiClient.patch<{ updated: number }>(
      API_ENDPOINTS.PENILAIAN.COMPANY_KPI_REALISASI,
      data
    )
    return res.data
  },
}
